// Beheer (enkel admin): /beheer/dagboek, /beheer/z-tickets, /beheer/pdf, /beheer/centralisatie
// Foto's dagontvangsten_1/2/3.png en 3_z_tickets.png
import { useCallback, useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { api, download, euro, foutTekst, getal, vandaagISO } from "../api";
import { Kop, WettelijkeVereisten } from "../components/Onderdelen";
import PdfArchief from "../components/PdfArchief";
import ZTickets from "../components/ZTickets";

const maandStart = () => vandaagISO().slice(0, 8) + "01";

function useBeheer() {
  const [me, setMe] = useState(null);
  const [vid, setVid] = useState(null);
  const [van, setVan] = useState(maandStart());
  const [tot, setTot] = useState(vandaagISO());
  useEffect(() => {
    api.get("/me").then((r) => { setMe(r.data); setVid(r.data.vestigingen?.[0]?.id); }).catch(() => setMe({ rol: "?" }));
  }, []);
  return { me, vid, setVid, van, setVan, tot, setTot };
}

function Navigatie({ me, vid, setVid, van, setVan, tot, setTot, metVestiging = true }) {
  const link = ({ isActive }) => `px-4 py-2.5 rounded-xl text-sm font-semibold border ${isActive ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200"}`;
  return (
    <div className="px-5 pt-3 flex gap-2 flex-wrap items-center">
      <NavLink to="/beheer/dagboek" className={link}>📒 Dagboek</NavLink>
      <NavLink to="/beheer/z-tickets" className={link}>🧾 Z-tickets</NavLink>
      <NavLink to="/beheer/pdf" className={link}>📄 PDF-archief</NavLink>
      <NavLink to="/beheer/centralisatie" className={link}>🏢 Centralisatieboek</NavLink>
      <span className="flex items-center gap-1 text-sm ml-2">📅
        <input type="date" value={van} onChange={(e) => setVan(e.target.value)} className="border rounded-lg px-2 py-1.5" />–
        <input type="date" value={tot} onChange={(e) => setTot(e.target.value)} className="border rounded-lg px-2 py-1.5" />
      </span>
      {metVestiging && (
        <div className="ml-auto flex gap-2">
          {(me?.vestigingen || []).map((v) => (
            <button key={v.id} onClick={() => setVid(v.id)}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold border ${vid === v.id ? "bg-[#1e2a44] text-white border-[#1e2a44]" : "bg-white text-slate-600 border-slate-200"}`}>
              {v.korte_naam}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function GeenToegang({ me }) {
  if (!me) return null;
  return me.rol !== "admin" ? <div className="p-10 text-center text-slate-500">Enkel voor de beheerder.</div> : null;
}

const knop = "bg-blue-600 text-white font-semibold rounded-xl px-4 py-2.5 text-sm";

// ---------------- Dagboek ----------------
export function BeheerDagboek({ onAfmelden }) {
  const s = useBeheer();
  const [data, setData] = useState(null);
  const [keten, setKeten] = useState(null);
  const [corr, setCorr] = useState(null);
  const [fout, setFout] = useState("");
  const [nieuwsteEerst, setNieuwsteEerst] = useState(true);
  const tarieven = s.me?.tarieven || [];
  const rijen = data ? [...data.rijen].sort((a, b) =>
    (nieuwsteEerst ? -1 : 1) * (a.datum.localeCompare(b.datum) || a.volgnummer - b.volgnummer)) : [];

  const laad = useCallback(async () => {
    if (!s.vid) return;
    try {
      setData((await api.get("/beheer/dagboek", { params: { vestiging_id: s.vid, van: s.van, tot: s.tot } })).data);
      setKeten(null); setFout("");
    } catch (e) { setFout(foutTekst(e)); }
  }, [s.vid, s.van, s.tot]);
  useEffect(() => { laad(); }, [laad]);

  if (s.me && s.me.rol !== "admin") return <GeenToegang me={s.me} />;
  const p = { vestiging_id: s.vid, van: s.van, tot: s.tot };

  const badge = (r) => {
    if (r.type === "dag") return <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-green-100 text-green-700">🔒 vergrendeld</span>;
    if (r.type === "gesloten") return <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">gesloten dag</span>;
    if (r.type === "afzonderlijk") return <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-indigo-100 text-indigo-800">&gt;€500</span>;
    return <span className="text-[11px] font-bold px-2 py-1 rounded-full bg-orange-100 text-orange-700">correctie → nr. {r.verwijst_naar}</span>;
  };

  return (
    <div className="min-h-screen bg-[#f1f4f9]">
      <Kop titel="Digitale dagontvangsten — beheer" sub="Conform KB nr. 1 art. 22 · Circulaire 2020/C/20" onAfmelden={onAfmelden} />
      <Navigatie {...s} />
      {fout && <div className="mx-5 mt-3 p-3 rounded-xl bg-red-50 text-red-700 text-sm">{fout}</div>}
      <div className="p-5 grid grid-cols-1 xl:grid-cols-[1.6fr_1fr] gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 p-4 overflow-auto">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div>
              <h3 className="font-bold text-slate-800">{data?.vestiging?.naam}</h3>
              <div className="text-xs text-slate-500">BTW {data?.vestiging?.btw} · {s.van} – {s.tot}</div>
            </div>
            <div className="ml-auto flex gap-2 flex-wrap">
              <button className="bg-blue-50 text-blue-600 font-semibold rounded-xl px-3 py-2.5 text-sm" onClick={() => setNieuwsteEerst((x) => !x)}>
                {nieuwsteEerst ? "↓ Nieuwste eerst" : "↑ Oudste eerst"}</button>
              <button className="bg-blue-50 text-blue-600 font-semibold rounded-xl px-3 py-2.5 text-sm" onClick={() => setCorr({ verwijst_naar: "", bedragen: {}, reden: "" })}>✎ Correctie toevoegen</button>
              <button className="bg-blue-50 text-blue-600 font-semibold rounded-xl px-3 py-2.5 text-sm"
                onClick={async () => { try { setKeten((await api.get("/beheer/dagboek/keten", { params: { vestiging_id: s.vid } })).data); } catch (e) { setFout(foutTekst(e)); } }}>🔐 Controleer hash-keten</button>
              <button className={knop} onClick={() => download("/beheer/dagboek/export", { ...p, formaat: "pdf" }, `dagontvangsten_${s.vid}_${s.van}_${s.tot}.pdf`)}>⬇ PDF</button>
              <button className={knop} onClick={() => download("/beheer/dagboek/export", { ...p, formaat: "csv" }, `dagontvangsten_${s.vid}_${s.van}_${s.tot}.csv`)}>⬇ CSV</button>
            </div>
          </div>
          {keten && (
            <div className={`mb-2 p-2 rounded-lg text-sm ${keten.intact ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
              {keten.intact ? `✓ Hash-keten intact (${keten.gecontroleerd} inschrijvingen)` : `✗ Fout in de keten bij nr. ${keten.fout_bij} (boek ${keten.boek})`}
            </div>
          )}
          <table className="w-full text-[13px] whitespace-nowrap">
            <thead><tr className="text-xs text-slate-500 border-b">
              <th className="text-left py-2">Nr.</th><th className="text-left">Datum</th>
              {tarieven.map((t) => <th key={t.id} className="text-right">{t.label}</th>)}
              <th className="text-right">Totaal</th><th className="text-left pl-3">Z</th><th className="text-left">Status</th>
            </tr></thead>
            <tbody>
              {rijen.map((r) => r.type === "afzonderlijk" ? (
                <tr key={r.boek + r.volgnummer} className="border-b border-slate-100">
                  <td className="py-2">{r.volgnummer}</td><td>{r.datum.split("-").reverse().join("/")}</td>
                  <td colSpan={tarieven.length + 1} className="text-xs text-slate-600 whitespace-normal">↳ {r.omschrijving} · {euro(r.totaal_eur)}</td>
                  <td className="pl-3">Z-{r.z_nummer}</td><td>{badge(r)}</td>
                </tr>
              ) : (
                <tr key={r.boek + r.volgnummer} className="border-b border-slate-100" title={r.omschrijving}>
                  <td className="py-2">{r.volgnummer}</td><td>{r.datum.split("-").reverse().join("/")}</td>
                  {tarieven.map((t) => <td key={t.id} className="text-right">{r.type === "gesloten" ? "—" : getal(r.bedragen_eur[t.id])}</td>)}
                  <td className="text-right font-bold">{getal(r.totaal_eur)}</td>
                  <td className="pl-3">{r.z_nummer ? `Z-${r.z_nummer}` : "—"}</td><td>{badge(r)}</td>
                </tr>
              ))}
              {data && (
                <tr className="bg-slate-50 font-bold">
                  <td colSpan={2} className="py-2">Totaal periode</td>
                  {tarieven.map((t) => <td key={t.id} className="text-right">{getal(data.totalen.bedragen_eur[t.id])}</td>)}
                  <td className="text-right">{getal(data.totalen.totaal_eur)}</td><td colSpan={2} />
                </tr>
              )}
            </tbody>
          </table>
          <div className="mt-3 p-2.5 rounded-lg bg-green-50 border border-green-200 text-xs text-green-700">
            🔐 Elke dag wordt na het Z-ticket vergrendeld en digitaal ondertekend (hash-keten). Wijzigen of wissen kan niet — fouten enkel via een nieuwe correctie-inschrijving.
          </div>
        </div>
        <WettelijkeVereisten />
      </div>

      {corr && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-3">Correctie-inschrijving</h3>
            <label className="text-sm">Verwijst naar inschrijving nr.</label>
            <input type="number" className="w-full border rounded-lg px-3 py-2 mb-2" value={corr.verwijst_naar}
              onChange={(e) => setCorr({ ...corr, verwijst_naar: e.target.value })} />
            {tarieven.map((t) => (
              <div key={t.id} className="flex items-center gap-2 mb-1">
                <label className="text-sm w-44">{t.label} (± €)</label>
                <input type="number" step="0.01" className="flex-1 border rounded-lg px-3 py-1.5"
                  value={corr.bedragen[t.id] ?? ""} onChange={(e) => setCorr({ ...corr, bedragen: { ...corr.bedragen, [t.id]: e.target.value } })} />
              </div>
            ))}
            <label className="text-sm">Reden</label>
            <textarea className="w-full border rounded-lg px-3 py-2 mb-3" value={corr.reden} onChange={(e) => setCorr({ ...corr, reden: e.target.value })} />
            <div className="flex gap-2 justify-end">
              <button className="px-4 py-2 rounded-xl bg-slate-100" onClick={() => setCorr(null)}>Annuleren</button>
              <button className={knop} onClick={async () => {
                try {
                  const bedragen = Object.fromEntries(Object.entries(corr.bedragen).map(([k, v]) => [k, Number(v || 0)]));
                  await api.post("/beheer/dagboek/correctie", { vestiging_id: s.vid, verwijst_naar: Number(corr.verwijst_naar), bedragen, reden: corr.reden });
                  setCorr(null); laad();
                } catch (e) { alert(foutTekst(e)); }
              }}>Inschrijven</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------- Z-tickets (beheerder: elke winkel) ----------------
export function BeheerZTickets({ onAfmelden }) {
  const s = useBeheer();
  if (s.me && s.me.rol !== "admin") return <GeenToegang me={s.me} />;
  return (
    <div className="min-h-screen bg-[#f1f4f9]">
      <Kop titel="Digitale dagontvangsten" sub="Opgeslagen Z-tickets per winkel" onAfmelden={onAfmelden} />
      <Navigatie {...s} />
      {s.vid && <ZTickets vid={s.vid} van={s.van} tot={s.tot} tarieven={s.me?.tarieven} />}
    </div>
  );
}

// ---------------- PDF-archief (beheerder: elke winkel + uploaden) ----------------
export function BeheerPdfArchief({ onAfmelden }) {
  const s = useBeheer();
  if (s.me && s.me.rol !== "admin") return <GeenToegang me={s.me} />;
  const v = s.me?.vestigingen?.find((x) => x.id === s.vid);
  return (
    <div className="min-h-screen bg-[#f1f4f9]">
      <Kop titel="Dagontvangsten — PDF-archief" sub="Per winkel · nieuwste bovenaan · voor boekhouder en controle" onAfmelden={onAfmelden} />
      <Navigatie {...s} />
      {s.vid && <PdfArchief vid={s.vid} vestiging={v} van={s.van} tot={s.tot} magUploaden />}
    </div>
  );
}

// ---------------- Centralisatieboek ----------------
export function BeheerCentralisatie({ onAfmelden }) {
  const s = useBeheer();
  const [c, setC] = useState(null);
  useEffect(() => {
    if (s.me?.rol !== "admin") return;
    api.get("/beheer/centralisatie", { params: { van: s.van, tot: s.tot } }).then((r) => setC(r.data));
  }, [s.me, s.van, s.tot]);
  if (s.me && s.me.rol !== "admin") return <GeenToegang me={s.me} />;
  const tarieven = s.me?.tarieven || [];
  return (
    <div className="min-h-screen bg-[#f1f4f9]">
      <Kop titel="Centralisatieboek" sub="Maatschappelijke zetel · alle vestigingen" onAfmelden={onAfmelden} />
      <Navigatie {...s} metVestiging={false} />
      <div className="p-5">
        <div className="bg-white rounded-2xl border border-slate-200 p-4">
          <div className="flex items-center mb-3">
            <h3 className="font-bold text-slate-800">Periode {s.van} – {s.tot}</h3>
            <button className={`${knop} ml-auto`} onClick={() => download("/beheer/centralisatie", { van: s.van, tot: s.tot, formaat: "pdf" }, `centralisatieboek_${s.van}_${s.tot}.pdf`)}>⬇ PDF</button>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-xs text-slate-500 border-b"><th className="text-left py-2">Vestiging</th>
              {tarieven.map((t) => <th key={t.id} className="text-right">{t.label}</th>)}<th className="text-right">Totaal</th></tr></thead>
            <tbody>
              {c && Object.entries(c.per_vestiging).map(([vid, v]) => (
                <tr key={vid} className="border-b border-slate-100">
                  <td className="py-2">{s.me?.vestigingen?.find((x) => x.id === vid)?.korte_naam}</td>
                  {tarieven.map((t) => <td key={t.id} className="text-right">{getal(v.bedragen_eur[t.id])}</td>)}
                  <td className="text-right font-bold">{getal(v.totaal_eur)}</td>
                </tr>
              ))}
              {c && (
                <tr className="bg-slate-50 font-bold"><td className="py-2">TOTAAL</td>
                  {tarieven.map((t) => <td key={t.id} className="text-right">{getal(c.totaal.bedragen_eur[t.id])}</td>)}
                  <td className="text-right">{getal(c.totaal.totaal_eur)}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
